
import React from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Church } from "lucide-react";

const formSchema = z.object({
  churchName: z.string().min(2, { message: "Church name is required" }),
  churchCity: z.string().min(2, { message: "Church city is required" }),
  contactName: z.string().min(2, { message: "Contact name is required" }),
  contactPhone: z.string()
    .min(10, { message: "Phone number must be at least 10 digits" })
    .refine((val) => /^[0-9()-.\s]+$/.test(val), { 
      message: "Phone number can only contain digits, parentheses, hyphens and spaces" 
    }),
  contactEmail: z.string().email({ message: "Valid email is required" }),
  additionalInfo: z.string().optional(),
});

export type ChurchFormValues = z.infer<typeof formSchema>;

interface ContactFormViewProps {
  onSubmit: (data: ChurchFormValues) => Promise<void>;
  onClose: () => void;
  isSubmitting: boolean;
  errorMessage: string | null;
  defaultEmail: string;
  primaryColor: string;
}

export function ContactFormView({ 
  onSubmit, 
  onClose, 
  isSubmitting, 
  errorMessage,
  defaultEmail,
  primaryColor
}: ContactFormViewProps) {
  const form = useForm<ChurchFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      churchName: "",
      churchCity: "",
      contactName: "",
      contactPhone: "",
      contactEmail: defaultEmail || "",
      additionalInfo: "",
    },
  });

  // Format phone number as user types
  const formatPhoneNumber = (value: string) => {
    // Strip all non-numeric characters
    const numbers = value.replace(/\D/g, "");
    
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 6) {
      return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    } else {
      return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
    }
  };

  const handleFormSubmit = async (data: ChurchFormValues) => {
    await onSubmit(data);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4">
        <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg mb-4 text-blue-800">
          <Church className="h-5 w-5 flex-shrink-0" />
          <p className="text-sm">Complete this form to receive information about our custom church plan with analytics, branded features, and sermon plans based on your congregation's needs.</p>
        </div>
        
        {errorMessage && (
          <div className="p-3 bg-red-50 border border-red-100 rounded text-red-600 text-sm">
            {errorMessage}
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="churchName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Church Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter your church's name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="churchCity"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Church City</FormLabel>
                <FormControl>
                  <Input placeholder="City location" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <FormField
          control={form.control}
          name="contactName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Your Name</FormLabel>
              <FormControl>
                <Input placeholder="Your full name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="contactPhone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <Input 
                    type="tel" 
                    placeholder="(555) 123-4567" 
                    {...field} 
                    onChange={(e) => {
                      // Format the phone number when the user types
                      const formatted = formatPhoneNumber(e.target.value);
                      field.onChange(formatted);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="contactEmail"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input 
                    type="email" 
                    placeholder="your.email@example.com" 
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <FormField
          control={form.control}
          name="additionalInfo"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Additional Information (Optional)</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Any specific requirements or questions" 
                  className="min-h-[100px]" 
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="flex justify-end space-x-4 pt-4">
          <Button 
            type="button" 
            variant="outline" 
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            disabled={isSubmitting}
            style={{ backgroundColor: primaryColor }}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit Inquiry"
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}
